import { Field, Label } from '@headlessui/react'
import dayjs from 'dayjs'
import PropTypes from 'prop-types'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { CheckBox } from '@/components/forms/CheckBox'
import { FileUploader } from '@/components/forms/FileUploader'
import { Textarea } from '@/components/forms/Textarea'
import { TextInput } from '@/components/forms/TextInput'
import { DocumentCheckIcon, TrashIcon } from '@/components/icons'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import {
  updateBillingCycle,
  createCleanupDetail,
  updateCleanupDetail,
  deleteCleanupDetail,
  createBillingCycleFile,
  deleteBillingCycleFile,
} from '@/services/billing.service'
import { UserStoreContext } from '@/stores/UserStore'
import { toast } from '@/utils/helpers'

import CleanupDetailItem from './CleanupDetailItem'

const CHECKBOX_VALUES = [
  { id: 'isCanadaDealer', label: 'Canada Dealer' },
  { id: 'isCleanup', label: 'Clean Up' },
  { id: 'isFinalBilling', label: 'Final Billing' },
  { id: 'isDealerReimbursement', label: 'Dealer Reimbursements' },
]

const DEFAULT_FORM_VALUES = {
  isCanadaDealer: false,
  isCleanup: false,
  isFinalBilling: false,
  isDealerReimbursement: false,
  cleanupApprovedBy: '',
  notesForBilling: '',
}

const EMPTY_CLEANUP_ITEM = {
  date: null,
  schedule: '',
  hoursSpent: '',
  numberOfClaims: '',
  amountPerHour: '',
  id: 'temp-0',
}

/**
 *
 * ApprovalModal
 *
 */
const ApprovalModal = ({
  showModal,
  setShowModal,
  dealerId,
  billingCycleId,
  getUpdatedBillingCycle,
  selectedBillingCycle,
}) => {
  // User context
  const { isWpcExecutive } = useContext(UserStoreContext)

  // State
  const [loading, setLoading] = useState(false)
  const [cleanupDetails, setCleanupDetails] = useState([])
  const [billingCycleFiles, setBillingCycleFiles] = useState([])
  const [isDeleteFileModalOpen, setIsDeleteFileModalOpen] = useState(false)
  const [selectedFileToDelete, setSelectedFileToDelete] = useState(null)

  const handleSuccess = (message) => toast(message, 'success')
  const handleError = (message) => toast(message, 'error')

  const {
    handleSubmit,
    register,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: DEFAULT_FORM_VALUES,
  })

  const handleCloseModal = () => {
    setShowModal(false)
    // Clear form data after modal closes (with slight delay to let animation finish)
    setTimeout(() => {
      reset(DEFAULT_FORM_VALUES)
      setCleanupDetails([])
      setBillingCycleFiles([])
    }, 250) // Slightly longer than the 200ms leave animation
    getUpdatedBillingCycle()
  }

  const handleCleanupDetailChange = useCallback((id, field, value) => {
    setCleanupDetails((prevDetails) => {
      const newDetails = prevDetails.map((detail) =>
        detail.id === id ? { ...detail, [field]: value } : detail,
      )
      return newDetails
    })
  }, [])

  const addCleanupDetail = () => {
    const newItem = {
      ...EMPTY_CLEANUP_ITEM,
      id: `temp-${Date.now()}`,
    }
    setCleanupDetails((prevDetails) => [...prevDetails, newItem])
  }

  const deleteCleanupDetailItem = async (id) => {
    const itemIndex = cleanupDetails.findIndex((detail) => detail.id === id)

    // Prevent deletion of the first item or if only one item remains
    if (itemIndex === 0 || cleanupDetails.length <= 1) {
      handleError('You must have at least one cleanup detail.')
      return
    }

    const isTemporary = id.toString().includes('temp')

    if (isTemporary) {
      // Remove temporary item immediately
      setCleanupDetails((prevDetails) => prevDetails.filter((detail) => detail.id !== id))
      handleSuccess('Cleanup detail removed.')
    } else {
      // Delete from server
      await deleteCleanupDetail(
        dealerId,
        billingCycleId,
        id,
        setLoading,
        handleError,
        (message) => {
          handleSuccess(message)
          setCleanupDetails((prevDetails) => prevDetails.filter((detail) => detail.id !== id))
        },
      )
    }
  }

  useEffect(() => {
    // Only update form when modal is shown
    if (!showModal) {
      return
    }

    const hasCleanupDetails =
      selectedBillingCycle?.cleanupDetails && selectedBillingCycle.cleanupDetails.length > 0

    reset({
      isCanadaDealer: selectedBillingCycle.isCanadaDealer || false,
      isCleanup: selectedBillingCycle.isCleanup || false,
      isFinalBilling: selectedBillingCycle.isFinalBilling || false,
      isDealerReimbursement: selectedBillingCycle.isDealerReimbursement || false,
      cleanupApprovedBy: selectedBillingCycle.cleanupApprovedBy || '',
      notesForBilling: selectedBillingCycle.notesForBilling || '',
    })

    // Set cleanup details or create empty one if isCleanup but no details
    if (hasCleanupDetails) {
      // Clean up any invalid dates from server data and transform field names
      const cleanedDetails = selectedBillingCycle.cleanupDetails.map((detail) => ({
        ...detail,
        date:
          detail.date === 'Invalid Date' ||
          (detail.date && detail.date.toString().includes('invalid'))
            ? null
            : detail.date,

        // Ensure proper string conversion
        hoursSpent: detail.hoursSpent ? detail.hoursSpent.toString() : '',
        numberOfClaims: detail.numberOfClaims ? detail.numberOfClaims.toString() : '',
        amountPerHour: detail.amountPerHour ? detail.amountPerHour.toString() : '',
      }))
      setCleanupDetails(cleanedDetails)
    } else if (selectedBillingCycle.isCleanup) {
      setCleanupDetails([{ ...EMPTY_CLEANUP_ITEM, id: `temp-${Date.now()}` }])
    } else {
      setCleanupDetails([])
    }

    // Set billing cycle files
    setBillingCycleFiles(selectedBillingCycle?.files || [])
  }, [showModal, reset, selectedBillingCycle])

  // Watch for isCleanup changes to manage cleanup details
  const isCleanupValue = watch('isCleanup')
  useEffect(() => {
    // Only manage cleanup details if we're not in the middle of loading existing data
    if (selectedBillingCycle?.cleanupDetails?.length > 0) {
      // Don't interfere when we have existing cleanup details from server
      return
    }

    if (isCleanupValue && cleanupDetails.length === 0) {
      // Add an empty cleanup detail when cleanup is checked and none exist
      setCleanupDetails([{ ...EMPTY_CLEANUP_ITEM, id: `temp-${Date.now()}` }])
    } else if (!isCleanupValue) {
      // Clear cleanup details when cleanup is unchecked (but only if no existing data)
      setCleanupDetails([])
    }
  }, [isCleanupValue, cleanupDetails.length, selectedBillingCycle?.cleanupDetails?.length])

  /**
   * Validates cleanup details have all required fields
   */
  const validateCleanupDetails = () => {
    if (!watch('isCleanup') || cleanupDetails.length === 0) {
      return true
    }

    const validationErrors = []
    cleanupDetails.forEach((detail, index) => {
      const missingFields = []

      if (!detail.date) missingFields.push('Date')
      if (!detail.schedule || detail.schedule.trim() === '') missingFields.push('Schedule')
      if (!detail.numberOfClaims || detail.numberOfClaims.toString().trim() === '') {
        missingFields.push('Number of Claims')
      }
      if (!detail.hoursSpent || detail.hoursSpent.toString().trim() === '') {
        missingFields.push('Hours Spent')
      }
      if (!detail.amountPerHour || detail.amountPerHour.toString().trim() === '') {
        missingFields.push('Amount Per Hour')
      }

      if (missingFields.length > 0) {
        validationErrors.push(`Cleanup Detail ${index + 1}: ${missingFields.join(', ')} required`)
      }
    })

    if (validationErrors.length > 0) {
      validationErrors.forEach((error) => handleError(error))
      return false
    }

    return true
  }

  /**
   * Handles saving cleanup details to the server
   */
  const saveCleanupDetails = async () => {
    if (!watch('isCleanup') || cleanupDetails.length === 0) {
      return true
    }

    // Validate cleanup details before saving
    if (!validateCleanupDetails()) {
      return false
    }

    try {
      await Promise.all(
        cleanupDetails.map(
          async (detail) =>
            new Promise((resolve, reject) => {
              const isTemporary = detail.id.toString().includes('temp')
              const payload = {
                date: detail.date ? dayjs(detail.date).format('YYYY-MM-DD') : null,
                schedule: detail.schedule,
                hoursSpent: detail.hoursSpent,
                numberOfClaims: detail.numberOfClaims,
                amountPerHour: detail.amountPerHour,
              }

              if (isTemporary) {
                createCleanupDetail(
                  dealerId,
                  billingCycleId,
                  payload,
                  setLoading,
                  (error) => {
                    handleError(error)
                    reject(error)
                  },
                  () => {
                    resolve()
                  },
                )
              } else {
                updateCleanupDetail(
                  dealerId,
                  billingCycleId,
                  detail.id,
                  payload,
                  setLoading,
                  (error) => {
                    handleError(error)
                    reject(error)
                  },
                  () => {
                    resolve()
                  },
                )
              }
            }),
        ),
      )
      return true
    } catch (error) {
      // Error already handled by handleError in the reject callbacks
      return false
    }
  }

  /**
   * Handles updating the billing cycle.
   */
  const onSubmit = async (data) => {
    const payload = { ...data, markAsSubmittedToBilling: true }

    // First save cleanup details if cleanup is enabled
    if (payload.isCleanup) {
      const cleanupSuccess = await saveCleanupDetails()
      if (!cleanupSuccess) {
        return
      }
    }

    // Then update the billing cycle
    updateBillingCycle(dealerId, billingCycleId, payload, setLoading, handleError, () => {
      handleSuccess('Credit memos submitted to Billing.')
      handleCloseModal()
    })
  }

  return (
    <Modal
      open={showModal}
      title="Approval Notes"
      setOpen={setShowModal}
      loading={loading}
      content={
        <div className="my-4 flex h-full flex-col gap-4">
          <Field>
            <Label className="text-charcoal text-xs font-semibold uppercase leading-[14px] tracking-[1.5px]">
              Does this billing cycle require any of the following?
            </Label>
            <div className="mt-2 space-y-2">
              {CHECKBOX_VALUES.map((item) => (
                <CheckBox key={item.id} {...register(item.id)} label={item.label} />
              ))}
            </div>
          </Field>
          {/* Conditional Cleanup Fields */}
          {watch('isCleanup') && (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <p className="text-charcoal text-xs font-semibold uppercase leading-[14px] tracking-[1.5px]">
                    Cleanup Details
                  </p>
                  <Button
                    background="bg-background"
                    onClick={addCleanupDetail}
                    outlined
                    label="Add Detail"
                    size="sm"
                    disabled={loading}
                    dataTestId="addCleanupDetail"
                  />
                </div>

                {cleanupDetails.map((item, index) => (
                  <CleanupDetailItem
                    key={item.id}
                    item={item}
                    canEdit
                    canDelete={cleanupDetails.length > 1 && index > 0}
                    loading={loading}
                    onDelete={deleteCleanupDetailItem}
                    onChange={handleCleanupDetailChange}
                  />
                ))}

                {cleanupDetails.length === 0 && (
                  <p className="my-3 text-center text-gray-500">No cleanup details added.</p>
                )}
              </div>

              <TextInput
                data-testid="cleanupApprovedBy"
                label="Approved By (Dealer)"
                error={errors.cleanupApprovedBy && 'This field is required'}
                fullWidth
                required
                {...register('cleanupApprovedBy', {
                  required: 'This field is required',
                })}
              />
            </div>
          )}

          {/* File Upload Section - Executives Only */}
          {isWpcExecutive && (
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-1 gap-14 md:grid-cols-2">
                <div>
                  <p className="text-charcoal text-xs font-semibold uppercase leading-[14px] tracking-[1.5px]">
                    Additional Files
                  </p>
                  <div>
                    {billingCycleFiles
                      .filter((file) => file && file.file)
                      .map((file) => (
                        <div key={file.id} className="mb-2 flex justify-between text-blue-800">
                          <div className="flex items-center overflow-x-hidden">
                            <DocumentCheckIcon className="mr-2 h-5 w-5" />
                            <a
                              className="underline"
                              href={file.file}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {file.displayName || 'Unnamed File'}
                            </a>
                          </div>
                          <button
                            aria-label="Delete"
                            type="button"
                            onClick={() => {
                              setSelectedFileToDelete(file)
                              setIsDeleteFileModalOpen(true)
                            }}
                            className="inline-flex items-center rounded-lg bg-transparent text-center text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-4 focus:ring-gray-300 disabled:cursor-not-allowed"
                            disabled={loading}
                          >
                            <TrashIcon className="text-charcoal-800 h-5 w-5" />
                          </button>
                        </div>
                      ))}

                    {billingCycleFiles.length === 0 ? (
                      <p className="my-3 text-center">No additional files uploaded.</p>
                    ) : null}

                    <Modal
                      open={isDeleteFileModalOpen}
                      title="Are you sure?"
                      setOpen={setIsDeleteFileModalOpen}
                      content={
                        <div className="my-8 flex h-full items-center justify-center">
                          <p className="text-center">
                            {`Please confirm that you'd like to delete this file?`}
                          </p>
                        </div>
                      }
                      width="small"
                      actions={[
                        {
                          type: 'submit',
                          label: 'Delete',
                          onClick: async () => {
                            const fileId = selectedFileToDelete.id
                            await deleteBillingCycleFile(
                              dealerId,
                              billingCycleId,
                              fileId,
                              setLoading,
                              handleError,
                              (message) => {
                                handleSuccess(message)
                                setBillingCycleFiles((prevFiles) =>
                                  prevFiles.filter((file) => file.id !== fileId),
                                )
                                setIsDeleteFileModalOpen(false)
                              },
                            )
                          },
                        },
                        { type: 'cancel', label: 'Cancel' },
                      ]}
                    />
                  </div>
                </div>

                <div className="bg-white-light rounded-md border-[1px]">
                  <div className="p-4">
                    <div className="border-[1px] border-dotted border-blue-500 px-2">
                      <FileUploader
                        label="Upload Additional Files"
                        autoSave
                        acceptedFileTypes={[
                          'image/*',
                          'application/pdf',
                          'application/vnd.ms-excel',
                          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                          'application/msword',
                          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                          'application/vnd.ms-powerpoint',
                          'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                        ]}
                        maxFiles={1}
                        id="billingCycleFile"
                        type="manual"
                        className="bg-white-light min-h-130 mb-0"
                        handleUploadToServer={async (file) => {
                          const formData = new FormData()
                          formData.append('file', file)
                          formData.append('displayName', file.name)
                          await createBillingCycleFile(
                            dealerId,
                            billingCycleId,
                            formData,
                            setLoading,
                            handleError,
                            (message, newBillingCycleFile) => {
                              handleSuccess(message)
                              if (newBillingCycleFile) {
                                setBillingCycleFiles((prevFiles) => [
                                  ...prevFiles,
                                  newBillingCycleFile,
                                ])
                                // Also update the parent billing cycle data
                                getUpdatedBillingCycle()
                              }
                            },
                          )
                        }}
                        disabled={loading}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Notes Field */}
          <Textarea
            {...register('notesForBilling')}
            className="w-full rounded-md border p-2"
            label="Notes for Billing"
            rows={3}
          />
        </div>
      }
      actions={[
        {
          type: 'submit',
          label: 'Submit',
          onClick: handleSubmit(onSubmit),
        },
        { type: 'cancel', label: 'Cancel', onClick: handleCloseModal },
      ]}
      onClose={handleCloseModal}
    />
  )
}

export default ApprovalModal

ApprovalModal.propTypes = {
  showModal: PropTypes.bool.isRequired,
  setShowModal: PropTypes.func.isRequired,
  dealerId: PropTypes.string.isRequired,
  billingCycleId: PropTypes.string.isRequired,
  getUpdatedBillingCycle: PropTypes.func.isRequired,
  selectedBillingCycle: PropTypes.object.isRequired,
}
